import styled from "styled-components";
import FocusTrap from "focus-trap-react";
import { useSideSheetStore } from "../../components/SideSheet/useSideSheetStore";
import { SideSheetContainer as Container, IconClose } from "cdk-radial";
import toTestId from "../../utils/toTestId";
import * as Styles from "../../core/styles";
import { RoundedIconButton } from "../../../components/Buttons/IconButton";
import { useEffect, useRef } from "react";


const StyledSideSheetContainer = styled(Container)`
  border-radius: 16px;
  border: ${({ theme }) => (theme.darkMode ? "2px solid gray" : "none")};
  bottom: 16px;
  box-shadow: ${({ theme }) =>
    theme.darkMode ? "none" : "0px 8px 25px 2px rgba(0, 0, 0, 0.35)"};
  font-family: "Roboto", "Montserrat",  sans-serif;
  height: calc(100vh - 64px);
  margin-right: ${({ isCollapsed }) => (isCollapsed ? "-450px" : 0)};
  position: fixed;
  right: 8px;
  top: 56px;
  z-index: 1002;
  overflow: hidden;
  width: ${({extendedWidth}) => extendedWidth ? "840px" : "420px" };
  @media (max-width: 470px) {
    width: 100vw;
    border-radius: 0px;
    right: 0px;
    top: 52px;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3); // Dark overlay
  display: ${({ isOpen }) => (isOpen ? "block" : "none")}; // Hide when closed
  z-index: 999;
`;

export function SideSheetHeader({
  title, dataTestId
}: {
  title: string;
  dataTestId?: string
}) {
  const { closeSideSheet } = useSideSheetStore(); 
  const testId = toTestId(`sidesheet-close-icon-${title}`);
  return (
    <Styles.SideSheetHeader>
      <div data-testid={dataTestId ? `${dataTestId}-header-title` : `sidesheet-header-title`}>{title}</div>
      <RoundedIconButton
        dataTestId={testId}
        darkMode
        icon={<IconClose />}
        onClick={closeSideSheet}
        id="sidesheet-close-button"
        text=""
      />
    </Styles.SideSheetHeader>
  );
}

const SideSheetContainer = () => {
  const { isOpen, isCollapsed, content, extendedWidth, closeSideSheet } = useSideSheetStore();
    const prevSideSheetContent = useRef(content);
    
    useEffect(() => {
      if (prevSideSheetContent.current && !content) {
        const timer = setTimeout(() => {
          const active = document.activeElement as HTMLElement | null;
          active?.blur();
          active?.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
        }, 100);
        prevSideSheetContent.current = content;
        return () => clearTimeout(timer);
      }
      prevSideSheetContent.current = content;
    }, [content]);

  return (
    <>
      <Overlay isOpen={isOpen} onClick={closeSideSheet} />
      <StyledSideSheetContainer
        isCollapsed={isCollapsed}
        extendedWidth={extendedWidth}
        data-testid="side-sheet"
      >
        <FocusTrap
          active={isOpen}
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            tabbableOptions: { displayCheck: 'none' },
          }}
        >
          <div style={{ display: 'contents' }}>
            {content?.title && <SideSheetHeader title={content.title} dataTestId={content.dataTestId} /> }
            {content?.body}
          </div>
        </FocusTrap>
      </StyledSideSheetContainer>
    </>
  );
};

export default SideSheetContainer;
